/**
 * Ashtakoot (Gun Milan) doctrine tables — research-verified, snapshot-frozen.
 *
 * ── Provenance ─────────────────────────────────────────────────────────────
 * Every table here was cross-verified cell-by-cell against 3–4 independent
 * published sources by a dedicated research pass (2026-11-03). Primary:
 * the Saravali documentation of the open-source Maitreya software
 * (saravali.github.io, CC BY-SA 4.0, Muhurta Chintamani tradition).
 * Corroborators: drikpanchang.com, onlinejyotish.com, astrolozyy.com.
 * Where published authorities disagree, the adopted reading and the
 * variants are recorded in DISPUTED_NOTES and in the research report
 * (REDESIGN_DECISIONS.md D-55); the UI shows the dispute, never hides it.
 *
 * ── Convention (read before touching anything) ─────────────────────────────
 * ALL two-dimensional tables are BRIDE-first: MATRIX[brideClass][groomClass].
 * Nakshatra index 0 = Ashwini … 26 = Revati. Rashi index 0 = Aries …
 * 11 = Pisces. Moon sign and Moon nakshatra only.
 *
 * ── Freeze rule (HANDOFF.md §9) ────────────────────────────────────────────
 * A single wrong cell produces a plausible 36-point total that people make
 * marriage decisions on. These tables are snapshot-frozen by test; any
 * edit must update the snapshot deliberately and be re-verified against
 * the sources above, pending Deepa's doctrine sign-off.
 */

export type Varna = 'Brahmin' | 'Kshatriya' | 'Vaishya' | 'Shudra';
export type VashyaClass = 'Chatushpada' | 'Nara' | 'Jalachara' | 'Vanachara' | 'Keeta';
export type Gana = 'Deva' | 'Manushya' | 'Rakshasa';
export type Nadi = 'Adi' | 'Madhya' | 'Antya';
export type Yoni =
  | 'Horse' | 'Elephant' | 'Sheep' | 'Serpent' | 'Dog' | 'Cat' | 'Rat'
  | 'Cow' | 'Buffalo' | 'Tiger' | 'Deer' | 'Monkey' | 'Mongoose' | 'Lion';
export type Graha = 'Sun' | 'Moon' | 'Mars' | 'Mercury' | 'Jupiter' | 'Venus' | 'Saturn';

export const ASHTAKOOT_CONVENTION =
  'Standard North-Indian Ashtakoot (Muhurta Chintamani tradition), Moon sign ' +
  'and Moon nakshatra only, scored bride-against-groom as the classical ' +
  'tables direct. Where published authorities disagree on a cell, we follow ' +
  'the majority of our verified sources and say so in the result.';

/** Gana / Nadi / Yoni by nakshatra, 0 = Ashwini … 26 = Revati. */
export const NAKSHATRA_ATTRS: ReadonlyArray<{ gana: Gana; nadi: Nadi; yoni: Yoni }> = [
  /* Ashwini          */ { gana: 'Deva',     nadi: 'Adi',    yoni: 'Horse' },
  /* Bharani          */ { gana: 'Manushya', nadi: 'Madhya', yoni: 'Elephant' },
  /* Krittika         */ { gana: 'Rakshasa', nadi: 'Antya',  yoni: 'Sheep' },
  /* Rohini           */ { gana: 'Manushya', nadi: 'Antya',  yoni: 'Serpent' },
  /* Mrigashira       */ { gana: 'Deva',     nadi: 'Madhya', yoni: 'Serpent' },
  /* Ardra            */ { gana: 'Manushya', nadi: 'Adi',    yoni: 'Dog' },
  /* Punarvasu        */ { gana: 'Deva',     nadi: 'Adi',    yoni: 'Cat' },
  /* Pushya           */ { gana: 'Deva',     nadi: 'Madhya', yoni: 'Sheep' },
  /* Ashlesha         */ { gana: 'Rakshasa', nadi: 'Antya',  yoni: 'Cat' },
  /* Magha            */ { gana: 'Rakshasa', nadi: 'Antya',  yoni: 'Rat' },
  /* Purva Phalguni   */ { gana: 'Manushya', nadi: 'Madhya', yoni: 'Rat' },
  /* Uttara Phalguni  */ { gana: 'Manushya', nadi: 'Adi',    yoni: 'Cow' },
  /* Hasta            */ { gana: 'Deva',     nadi: 'Adi',    yoni: 'Buffalo' },
  /* Chitra           */ { gana: 'Rakshasa', nadi: 'Madhya', yoni: 'Tiger' },
  /* Swati            */ { gana: 'Deva',     nadi: 'Antya',  yoni: 'Buffalo' },
  /* Vishakha         */ { gana: 'Rakshasa', nadi: 'Antya',  yoni: 'Tiger' },
  /* Anuradha         */ { gana: 'Deva',     nadi: 'Madhya', yoni: 'Deer' },
  /* Jyeshtha         */ { gana: 'Rakshasa', nadi: 'Adi',    yoni: 'Deer' },
  /* Mula             */ { gana: 'Rakshasa', nadi: 'Adi',    yoni: 'Dog' },
  /* Purva Ashadha    */ { gana: 'Manushya', nadi: 'Madhya', yoni: 'Monkey' },
  /* Uttara Ashadha   */ { gana: 'Manushya', nadi: 'Antya',  yoni: 'Mongoose' },
  /* Shravana         */ { gana: 'Deva',     nadi: 'Antya',  yoni: 'Monkey' },
  /* Dhanishtha       */ { gana: 'Rakshasa', nadi: 'Madhya', yoni: 'Lion' },
  /* Shatabhisha      */ { gana: 'Rakshasa', nadi: 'Adi',    yoni: 'Horse' },
  /* Purva Bhadrapada */ { gana: 'Manushya', nadi: 'Adi',    yoni: 'Lion' },
  /* Uttara Bhadrapada*/ { gana: 'Manushya', nadi: 'Madhya', yoni: 'Cow' },
  /* Revati           */ { gana: 'Deva',     nadi: 'Antya',  yoni: 'Elephant' },
];

/** Varna by rashi, 0 = Aries … 11 = Pisces.
 *  Adopted sign-assignment follows the 3-source majority (Drik Panchang,
 *  onlinejyotish, astrolozyy): Vaishya = earth signs, Shudra = air signs.
 *  Saravali swaps those two — see DISPUTED_NOTES.varna. */
export const SIGN_ATTRS: ReadonlyArray<{ varna: Varna }> = [
  { varna: 'Kshatriya' }, // Aries
  { varna: 'Vaishya' },   // Taurus
  { varna: 'Shudra' },    // Gemini
  { varna: 'Brahmin' },   // Cancer
  { varna: 'Kshatriya' }, // Leo
  { varna: 'Vaishya' },   // Virgo
  { varna: 'Shudra' },    // Libra
  { varna: 'Brahmin' },   // Scorpio
  { varna: 'Kshatriya' }, // Sagittarius
  { varna: 'Vaishya' },   // Capricorn
  { varna: 'Shudra' },    // Aquarius
  { varna: 'Brahmin' },   // Pisces
];

export const VARNA_RANK: Readonly<Record<Varna, number>> = {
  Brahmin: 4,
  Kshatriya: 3,
  Vaishya: 2,
  Shudra: 1,
};

/** Sign lords, 0 = Aries … 11 = Pisces (classical rulerships only). */
export const SIGN_LORDS: ReadonlyArray<Graha> = [
  'Mars', 'Venus', 'Mercury', 'Moon', 'Sun', 'Mercury',
  'Venus', 'Mars', 'Jupiter', 'Saturn', 'Saturn', 'Jupiter',
];

/**
 * Vashya class of a Moon placement. Two signs split at 15°:
 * Sagittarius (Nara 0–15°, Chatushpada 15–30°) and Capricorn
 * (Chatushpada 0–15°, Jalachara 15–30°) — the Drik Panchang splits,
 * corroborated by the 4-source consensus on every whole sign.
 */
export function vashyaClassOf(rashiIndex: number, degreeInRashi: number): VashyaClass {
  switch (rashiIndex) {
    case 0: return 'Chatushpada';  // Aries
    case 1: return 'Chatushpada';  // Taurus
    case 2: return 'Nara';         // Gemini
    case 3: return 'Jalachara';    // Cancer
    case 4: return 'Vanachara';    // Leo
    case 5: return 'Nara';         // Virgo
    case 6: return 'Nara';         // Libra
    case 7: return 'Keeta';        // Scorpio
    case 8: return degreeInRashi < 15 ? 'Nara' : 'Chatushpada';      // Sagittarius
    case 9: return degreeInRashi < 15 ? 'Chatushpada' : 'Jalachara'; // Capricorn
    case 10: return 'Nara';        // Aquarius
    case 11: return 'Jalachara';   // Pisces
    default: throw new Error(`rashiIndex out of range: ${rashiIndex}`);
  }
}

/** Vashya kuta, BRIDE class first: VASHYA_MATRIX[bride][groom] → 0–2.
 *  Saravali / Maitreya values — the most disputed Ashtakoot table; the
 *  published variants differ by at most 2 points of 36 (DISPUTED_NOTES). */
export const VASHYA_MATRIX: Readonly<Record<VashyaClass, Readonly<Record<VashyaClass, number>>>> = {
  Chatushpada: { Chatushpada: 2,   Nara: 0, Jalachara: 0, Vanachara: 0.5, Keeta: 0 },
  Nara:        { Chatushpada: 1,   Nara: 2, Jalachara: 1, Vanachara: 0.5, Keeta: 1 },
  Jalachara:   { Chatushpada: 0.5, Nara: 1, Jalachara: 2, Vanachara: 1,   Keeta: 1 },
  Vanachara:   { Chatushpada: 0,   Nara: 0, Jalachara: 0, Vanachara: 2,   Keeta: 0 },
  Keeta:       { Chatushpada: 1,   Nara: 1, Jalachara: 1, Vanachara: 0,   Keeta: 2 },
};

/** Row/column order of YONI_MATRIX. */
export const YONI_ORDER: ReadonlyArray<Yoni> = [
  'Horse', 'Elephant', 'Sheep', 'Serpent', 'Dog', 'Cat', 'Rat',
  'Cow', 'Buffalo', 'Tiger', 'Deer', 'Monkey', 'Mongoose', 'Lion',
];

/** Yoni kuta, BRIDE animal first: YONI_MATRIX[brideIdx][groomIdx] → 0–4.
 *  Saravali 14×14 (the only complete published matrix found). Mildly
 *  asymmetric by design in two cells — kept verbatim from the source.
 *  The seven sworn-enemy pairs all score 0. */
export const YONI_MATRIX: ReadonlyArray<ReadonlyArray<number>> = [
  /* bride ↓ groom →  Hor Ele She Ser Dog Cat Rat Cow Buf Tig Dee Mon Mng Lio */
  /* Horse    */ [4, 2, 2, 3, 2, 2, 2, 1, 0, 1, 3, 3, 2, 1],
  /* Elephant */ [2, 4, 3, 3, 2, 2, 2, 2, 3, 1, 2, 3, 2, 0],
  /* Sheep    */ [2, 3, 4, 2, 1, 2, 1, 3, 3, 1, 2, 0, 3, 1],
  /* Serpent  */ [3, 3, 2, 4, 2, 1, 1, 1, 1, 2, 2, 2, 0, 2],
  /* Dog      */ [2, 2, 1, 2, 4, 2, 1, 2, 2, 1, 0, 2, 1, 1],
  /* Cat      */ [2, 2, 2, 1, 2, 4, 0, 2, 2, 1, 3, 3, 2, 1],
  /* Rat      */ [2, 2, 1, 1, 1, 0, 4, 2, 2, 2, 2, 2, 1, 2],
  /* Cow      */ [1, 2, 3, 1, 2, 2, 2, 4, 3, 0, 3, 2, 2, 1],
  /* Buffalo  */ [0, 3, 3, 1, 2, 2, 2, 3, 4, 1, 2, 2, 2, 1],
  /* Tiger    */ [1, 1, 1, 2, 1, 1, 2, 0, 1, 4, 1, 1, 2, 1],
  /* Deer     */ [1, 2, 2, 2, 0, 3, 2, 3, 2, 1, 4, 2, 2, 1],
  /* Monkey   */ [3, 3, 0, 2, 2, 3, 2, 2, 2, 1, 2, 4, 3, 2],
  /* Mongoose */ [2, 2, 3, 0, 1, 2, 1, 2, 2, 2, 2, 3, 4, 2],
  /* Lion     */ [1, 0, 1, 2, 1, 1, 2, 1, 2, 1, 1, 2, 2, 4],
];

/** The seven classical sworn-enemy yoni pairs (each scores 0 both ways). */
export const YONI_ENEMY_PAIRS: ReadonlyArray<readonly [Yoni, Yoni]> = [
  ['Horse', 'Buffalo'], ['Elephant', 'Lion'], ['Sheep', 'Monkey'],
  ['Serpent', 'Mongoose'], ['Dog', 'Deer'], ['Cat', 'Rat'], ['Cow', 'Tiger'],
];

const MAITRI_ORDER: ReadonlyArray<Graha> = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];

/** Graha Maitri 7×7 — symmetric; derived from Parashara's permanent
 *  friendships with the standard combination scores (same lord or both
 *  friends 5; friend+neutral 4; both neutral 3; friend+enemy 1;
 *  neutral+enemy 0.5; both enemies 0). Verified cell-by-cell. */
const MAITRI_MATRIX: ReadonlyArray<ReadonlyArray<number>> = [
  /*            Sun  Moon Mars Merc Jup  Ven  Sat */
  /* Sun     */ [5,   5,   5,   4,   5,   0,   0],
  /* Moon    */ [5,   5,   4,   1,   4,   0.5, 0.5],
  /* Mars    */ [5,   4,   5,   0.5, 5,   3,   0.5],
  /* Mercury */ [4,   1,   0.5, 5,   0.5, 5,   4],
  /* Jupiter */ [5,   4,   5,   0.5, 5,   0.5, 3],
  /* Venus   */ [0,   0.5, 3,   5,   0.5, 5,   5],
  /* Saturn  */ [0,   0.5, 0.5, 4,   3,   5,   5],
];

/** Graha Maitri score for two sign lords (order-independent). */
export function GRAHA_MAITRI_SCORE(a: Graha, b: Graha): number {
  return MAITRI_MATRIX[MAITRI_ORDER.indexOf(a)]![MAITRI_ORDER.indexOf(b)]!;
}

/** Gana kuta, BRIDE gana first: GANA_MATRIX[bride][groom] → 0–6.
 *  Asymmetric by tradition (bride Manushya + groom Deva = 5, reversed = 6;
 *  bride Rakshasa + groom Deva = 1, reversed = 0).
 *  Saravali and onlinejyotish agree exactly on every cell. */
export const GANA_MATRIX: Readonly<Record<Gana, Readonly<Record<Gana, number>>>> = {
  Deva:     { Deva: 6, Manushya: 6, Rakshasa: 0 },
  Manushya: { Deva: 5, Manushya: 6, Rakshasa: 0 },
  Rakshasa: { Deva: 1, Manushya: 0, Rakshasa: 6 },
};

/** Short, honest dispute notes, surfaced verbatim in the tool UI. */
export const DISPUTED_NOTES = {
  varna:
    'Sources disagree on which signs are Vaishya and which are Shudra. We ' +
    'follow the majority reading (Taurus, Virgo and Capricorn as Vaishya); ' +
    'one respected tabulation swaps them, which can move this point.',
  vashya:
    'The Vashya table is the most debated in Ashtakoot — four published ' +
    'versions differ on a few half-point and one-point cells (never by more ' +
    'than 2 of the 36 total). We use the Muhurta Chintamani tabulation.',
  yoni:
    'A couple of Yoni pairs score slightly differently across sources, and ' +
    'the classical table is deliberately not mirror-symmetric. We use the ' +
    'one complete published 14×14 table, kept exactly as printed.',
  maitri:
    'Scored from Parashara’s permanent planetary friendships. Some ' +
    'software uses compound (temporal) friendship instead, which can shift ' +
    'this kuta for the same two charts.',
  gana:
    'The Gana table is directional: the same two nakshatras can score ' +
    'differently depending on who is bride and who is groom. A minority of ' +
    'sources soften the Manushya–Rakshasa cells.',
  nadi:
    'Several traditions exempt specific nakshatras from Nadi dosha, but the ' +
    'exemption lists themselves disagree. We report the dosha whenever both ' +
    'Moons share a nadi and leave the judgement to a reading.',
} as const;
